/**
 * Policy lines — the one client-side description of what a line is.
 *
 * XN-4535 made this page the single source for the policy-type question on
 * registration, the policy picker and tags on a record, and the segments in
 * Communications. That only holds if those surfaces stop keeping their own
 * lists: before this file there were three, and they had already drifted —
 * `segmentRuleHelpers.POLICY_TYPES`, `PolicyholderRecord.POLICY_TYPES` and the
 * label rules copied into `Users.vue`. A line called "Car insurance" while a
 * hard-coded list said "Auto" meant every Auto row failed import validation.
 *
 * Mirrors App\Models\PolicyLineTag on the server, which stays the authority —
 * these rules only decide what to draw and when to complain early.
 */

/** Spec: 1-40 characters, letters/digits/spaces/hyphens/slashes only. */
export const MAX_NAME_LENGTH = 40;
const NAME_PATTERN = /^[\p{L}\p{N} \-\/]+$/u;

/** What an unnamed line is called everywhere it has to be rendered. */
export const UNTITLED = 'Untitled policy line';

/** The six illustrations and five colours the editor offers, in order. */
export const ICONS = ['home', 'door', 'book', 'shield', 'gift', 'star'];
export const COLORS = ['#5E2F78', '#1F5E8C', '#166B48', '#7A5210', '#A32020'];

export const DEFAULT_ICON = 'shield';
export const DEFAULT_COLOR = COLORS[0];

/**
 * Charset and length only. Uniqueness needs the whole hub's list, so it is
 * checked where that is in hand — and again on the server, which is the only
 * check that settles it.
 */
export const validateName = (raw, { allowEmpty = false } = {}) => {
  const value = String(raw ?? '').trim();

  if (!value) return allowEmpty ? '' : 'Enter a name.';
  if (value.length > MAX_NAME_LENGTH) return `Policy line names are limited to ${MAX_NAME_LENGTH} characters.`;
  if (!NAME_PATTERN.test(value)) return 'Use only letters, numbers, spaces, hyphens and slashes.';

  return '';
};

/** Trim and collapse runs of whitespace, the way the server will. */
export const canonical = (raw) => String(raw ?? '').trim().replace(/\s+/g, ' ');

const normalize = (raw) => canonical(raw).toLowerCase();

/**
 * Is this name already taken by another line? Case-insensitive and
 * whitespace-insensitive, matching the server's uniqueness key.
 */
export const isDuplicateName = (name, lines, exceptId = null) => {
  const key = normalize(name);
  if (!key) return false;

  return lines.some((line) => line.id !== exceptId && normalize(line.name) === key);
};

/**
 * The lines a surface other than this page may offer: active and named. A draft
 * is the owner's unfinished thought — putting "Untitled policy line" on a
 * registration form would ask a stranger to pick something nobody has defined.
 */
export const published = (lines) => (lines ?? []).filter((line) => !line.archived && line.name);

/**
 * The lines a *policy* can be booked against: published, and carrying the SSO
 * policy type that the policy record is keyed on.
 *
 * A line without one is a real line — it tags a record and fills the
 * registration question — but it is NOT a segment source, despite an earlier
 * version of this comment saying so. Segments resolve through the policy
 * gateway, which only knows SSO policy types, so there is nothing for a keyless
 * line to match on.
 *
 * ponytail: every line gets a key once SSO's type list stops being a constant
 * (`Policy::TYPES` in xl-apps-web-ssoapi), and this distinction disappears.
 */
export const bookable = (lines) => published(lines).filter((line) => line.gateway_key);

/**
 * Options for a `policy_line` registration question, keeping whatever the
 * person already answered.
 *
 * A registration answer is stored as the line's NAME, so it is a snapshot of
 * the wording at the time it was answered — not a live link to the line. Rename
 * or archive that line and the stored value matches none of the current
 * options, and `pge-dropdown` handles an unmatched value by emitting
 * `undefined` on blur: opening a profile and closing it again would erase a
 * historical answer nobody meant to touch.
 *
 * So an unmatched answer is carried as its own option. It is shown plainly
 * rather than flagged — on a member's own profile, annotating their answer with
 * a note about hub configuration explains a thing that is not theirs to fix.
 */
export const answerOptions = (lines, current) => {
  const options = published(lines).map((line) => ({ label: line.name, value: String(line.id) }));
  const answered = String(current ?? '').trim();

  if (!answered || options.some((option) => option.value === answered)) {
    return options;
  }

  // An answer for a line that exists but is no longer offered — archived, or a
  // draft it was given against — still resolves to that line's name.
  const known = (lines ?? []).find((line) => String(line.id) === answered);

  return [
    { label: known?.name || answered, value: answered },
    ...options,
  ];
};

/**
 * What to show for a stored `policy_line` answer.
 *
 * Mirrors PolicyLineTagService::answerDisplay() on the server. An answer is the
 * line's id, so a rename reaches it; anything that is not a known id is handed
 * back untouched — an answer written before XN-4538 is the NAME, and a value
 * nothing resolves is still somebody's real answer, so neither is blanked.
 */
export const answerDisplay = (value, lines) => {
  const answered = String(value ?? '').trim();

  if (!answered) return answered;

  return (lines ?? []).find((line) => String(line.id) === answered)?.name ?? answered;
};
